import { useMemo, useState } from 'react';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useConfirm } from '../../../components/confirm';
import { fmtLocalDate } from '../../../utils/datetime';
import {
  useDivisionCertificates, useDeleteCertificate, useLinkableCertificates,
  useLinkCertificate, useUnlinkCertificate, errMessage, type CertificateRow,
} from './systemMasters';
import { CertificateDetailDialog } from './CertificateDetailDialog';

interface Props { companyId?: string; }

/**
 * Division detail ▸ Certificate tab — the one-to-many dbo.Certificates child collection.
 * Mirrors the legacy XAF Certificate grid (Item / Text / ExpirationDate / Comment /
 * LastUpdatedDate / LastUpdatedBy) and its full toolbar: New · Link · Unlink · Edit ·
 * Delete · Export. Link attaches an orphan certificate (Company IS NULL) to this division;
 * Unlink detaches it (keeps the certificate, making it linkable again).
 */
export function DivisionCertificatePanel({ companyId }: Props) {
  const notify = useNotify();
  const { confirm } = useConfirm();
  const { data: rows = [] } = useDivisionCertificates(companyId);
  const del = useDeleteCertificate(companyId ?? '');
  const link = useLinkCertificate(companyId ?? '');
  const unlink = useUnlinkCertificate(companyId ?? '');

  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [editCert, setEditCert] = useState<CertificateRow | null | undefined>(undefined);   // undefined = closed, null = New
  const [pickOpen, setPickOpen] = useState(false);
  const [pickSearch, setPickSearch] = useState('');
  const [checked, setChecked] = useState<Set<string | number>>(new Set());
  const [busy, setBusy] = useState(false);

  const { data: linkable = [] } = useLinkableCertificates(companyId, pickOpen);

  const editSelected = () => {
    if (selectedIds.size !== 1) { notify.warning('Select exactly one certificate to edit.'); return; }
    const c = rows.find((x) => x.id === String([...selectedIds][0])); if (c) setEditCert(c);
  };
  const doDelete = async () => {
    if (selectedIds.size === 0) { notify.warning('Select at least one certificate to delete.'); return; }
    if (!await confirm({ title: 'Delete', tone: 'danger', confirmLabel: 'Delete', message: `Delete ${selectedIds.size} certificate${selectedIds.size === 1 ? '' : 's'}?` })) return;
    try { for (const id of selectedIds) await del.mutateAsync(String(id)); setSelectedIds(new Set()); }
    catch (e) { notify.error(errMessage(e)); }
  };
  const doUnlink = async () => {
    if (selectedIds.size === 0) { notify.warning('Select at least one certificate to unlink.'); return; }
    if (!await confirm({ title: 'Unlink', message: `Remove ${selectedIds.size} certificate${selectedIds.size === 1 ? '' : 's'} from this division? (The certificate is kept.)` })) return;
    try { for (const id of selectedIds) await unlink.mutateAsync(String(id)); setSelectedIds(new Set()); }
    catch (e) { notify.error(errMessage(e)); }
  };

  const openPick = () => { setChecked(new Set()); setPickSearch(''); setPickOpen(true); };
  const pickRows = useMemo(() => {
    const q = pickSearch.trim().toLowerCase();
    return q ? linkable.filter((c) => [c.item, c.text].some((v) => (v ?? '').toLowerCase().includes(q))) : linkable;
  }, [linkable, pickSearch]);
  const applyPick = async () => {
    if (!companyId || checked.size === 0) { setPickOpen(false); return; }
    setBusy(true);
    try { for (const id of checked) await link.mutateAsync(String(id)); setPickOpen(false); }
    catch (e) { notify.error(errMessage(e)); }
    finally { setBusy(false); }
  };

  const columns: AppColumn<CertificateRow>[] = [
    {
      field: 'edit', header: '', sortable: false, filterable: false, width: 44, align: 'center',
      render: (c) => <button className="pts-icon-btn edit" title="Edit certificate" onClick={(e) => { e.stopPropagation(); setEditCert(c); }}><i className="ti ti-edit" /></button>,
    },
    // Equal-width columns (six data columns, ~16.6% each). LIMS sorts by the primary column only —
    // ONLY Item carries the sort symbol; the rest are filter-only.
    { field: 'item', header: 'Item', width: '17%', minWidth: 110, text: (r) => r.item ?? '' },
    { field: 'text', header: 'Text', width: '17%', minWidth: 110, sortable: false, text: (r) => r.text ?? '' },
    { field: 'expirationDate', header: 'ExpirationDate', width: '17%', minWidth: 110, sortable: false, text: (r) => (r.expirationDate ? fmtLocalDate(r.expirationDate) : '') },
    { field: 'comment', header: 'Comment', width: '17%', minWidth: 110, sortable: false, text: (r) => r.comment ?? '' },
    { field: 'lastUpdatedDate', header: 'LastUpdatedDate', width: '16%', minWidth: 110, sortable: false, text: (r) => (r.lastUpdatedDate ? fmtLocalDate(r.lastUpdatedDate) : '') },
    { field: 'lastUpdatedBy', header: 'LastUpdatedBy', width: '16%', minWidth: 110, sortable: false, text: (r) => r.lastUpdatedBy ?? '' },
  ];
  // Full LIMS picker columns (the "Certificates" lookup list view) — no persistent sort caret
  // (LIMS shows only the filter funnels); rows arrive ordered by Item from the API.
  const pickCols: AppColumn<CertificateRow>[] = [
    { field: 'item', header: 'Item', minWidth: 120, sortable: false, text: (r) => r.item ?? '' },
    { field: 'text', header: 'Text', minWidth: 140, sortable: false, text: (r) => r.text ?? '' },
    { field: 'expirationDate', header: 'ExpirationDate', width: 130, sortable: false, text: (r) => (r.expirationDate ? fmtLocalDate(r.expirationDate) : '') },
    { field: 'comment', header: 'Comment', minWidth: 120, sortable: false, text: (r) => r.comment ?? '' },
    { field: 'lastUpdatedDate', header: 'LastUpdatedDate', width: 130, sortable: false, text: (r) => (r.lastUpdatedDate ? fmtLocalDate(r.lastUpdatedDate) : '') },
    { field: 'lastUpdatedBy', header: 'LastUpdatedBy', minWidth: 110, sortable: false, text: (r) => r.lastUpdatedBy ?? '' },
  ];

  if (!companyId) return <div className="acc-tab-pane"><div style={{ padding: 14, color: '#8a7a66' }}>Save the division first to add certificates.</div></div>;

  return (
    <div className="acc-subgrid">
      <div className="acc-sub-toolbar">
        <button className="ca-btn" title="New" onClick={() => setEditCert(null)}><i className="ti ti-plus" /> New</button>
        <button className="ca-btn" title="Link" onClick={openPick}><i className="ti ti-link" /> Link</button>
        <button className="ca-btn" title="Unlink" onClick={() => void doUnlink()} disabled={selectedIds.size === 0}><i className="ti ti-unlink" /> Unlink</button>
        <button className="ca-btn" title="Edit" onClick={editSelected} disabled={selectedIds.size !== 1}><i className="ti ti-edit" /> Edit</button>
        <button className="ca-btn" title="Delete" onClick={() => void doDelete()} disabled={selectedIds.size === 0}><i className="ti ti-trash" /> Delete</button>
        <ExportMenu triggerClassName="ca-btn" filename="Division-Certificates" headers={['Item', 'Text', 'ExpirationDate', 'Comment', 'LastUpdatedDate', 'LastUpdatedBy']}
          rows={rows.map((r) => [r.item ?? '', r.text ?? '', r.expirationDate ? fmtLocalDate(r.expirationDate) : '', r.comment ?? '', r.lastUpdatedDate ? fmtLocalDate(r.lastUpdatedDate) : '', r.lastUpdatedBy ?? ''])} />
      </div>
      <AppDataGrid<CertificateRow> rows={rows} columns={columns} getRowId={(r) => r.id}
        selectable selectedIds={selectedIds} onSelectionChange={setSelectedIds} selectedRowId={editCert?.id ?? null}
        emptyText="No certificates."
        defaultPageSize={20} />

      {pickOpen && (
        <div className="acc-overlay" onMouseDown={(e) => { e.stopPropagation(); setPickOpen(false); }}>
          <div className="acc-dialog acc-dialog-pick" onMouseDown={(e) => e.stopPropagation()}>
            <div className="acc-dialog-hdr"><span>Certificates</span><i className="ti ti-x acc-dialog-x" onClick={() => setPickOpen(false)} /></div>
            <div className="acc-dialog-body">
              {/* LIMS lookup picker header: New on the left, Search on the right. New opens the Certificate form. */}
              <div className="acc-pick-bar">
                <button className="ca-btn" title="New" onClick={() => { setPickOpen(false); setEditCert(null); }}><i className="ti ti-plus" /> New</button>
                <div className="acc-search"><i className="ti ti-search" />
                  <input autoFocus placeholder="Search…" value={pickSearch} onChange={(e) => setPickSearch(e.target.value)} />
                  {pickSearch && <i className="ti ti-x acc-search-x" onClick={() => setPickSearch('')} />}
                </div>
              </div>
              <AppDataGrid<CertificateRow> rows={pickRows} columns={pickCols} getRowId={(r) => r.id}
                selectable selectedIds={checked} onSelectionChange={setChecked}
                emptyText="No unlinked certificates available."
                defaultPageSize={20} />
            </div>
            <div className="acc-dialog-ftr">
              <button className="ca-btn ca-btn-solid" onClick={() => void applyPick()} disabled={busy}>OK</button>
              <button className="ca-btn" onClick={() => setPickOpen(false)} disabled={busy}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
            </div>
          </div>
        </div>
      )}

      {editCert !== undefined && <CertificateDetailDialog companyId={companyId} certificate={editCert} onClose={() => setEditCert(undefined)} />}
    </div>
  );
}
